import React, { useMemo } from 'react';
import type { OmsRecycleOrderDailyStat } from 'apis/oms';
import LOCALS from 'commons/locals';
import { useTranslation } from 'react-i18next';
import { thousands } from 'utils/tools';

type RecycleOrderDailyStatsCardProps = {
  records?: OmsRecycleOrderDailyStat[];
};

type SummaryType = {
  type: number;
  labelKey: string;
  totalCount: number;
  amountByCurrency: Record<string, number>;
};

const SUMMARY_TYPES = [
  { type: 1, labelKey: LOCALS.consignment },
  { type: 2, labelKey: LOCALS.recycle },
];

const RecycleOrderDailyStatsCard: React.FC<RecycleOrderDailyStatsCardProps> = ({
  records = [],
}) => {
  const { t } = useTranslation();
  const summaries = useMemo<SummaryType[]>(() => {
    return SUMMARY_TYPES.map(({ type, labelKey }) => {
      const matchedRecords = records.filter((record) => record.type === type);

      return {
        type,
        labelKey,
        totalCount: matchedRecords.reduce(
          (total, record) => total + Number(record.count || 0),
          0,
        ),
        amountByCurrency: matchedRecords.reduce<Record<string, number>>(
          (amountMap, record) => {
            const currency = record.currency || '-';

            return {
              ...amountMap,
              [currency]:
                (amountMap[currency] || 0) + Number(record.totalAmount || 0),
            };
          },
          {},
        ),
      };
    });
  }, [records]);
  const hasRecords = records.length > 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-6 pb-4 border-b">
        {t(LOCALS.daily_register_close_recycle_order_daily_stats_title)}
      </h2>

      {hasRecords ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {summaries.map((summary) => (
            <div
              key={summary.type}
              className="rounded-lg border border-gray-200 bg-gray-50 p-4"
            >
              <div className="flex items-center justify-between gap-4">
                <span className="text-base font-semibold text-gray-900">
                  {t(summary.labelKey)}
                </span>
                <span className="text-lg font-bold text-gray-800">
                  {t('daily_register_close_recycle_order_daily_count', {
                    count: summary.totalCount,
                  })}
                </span>
              </div>

              <div className="mt-4 space-y-2">
                {Object.entries(summary.amountByCurrency).length > 0 ? (
                  Object.entries(summary.amountByCurrency).map(
                    ([currency, amount]) => (
                      <div
                        key={currency}
                        className="flex items-center justify-between gap-4 text-sm"
                      >
                        <span className="text-gray-600">{currency}</span>
                        <span className="font-medium text-gray-900">
                          {currency} {thousands(amount)}
                        </span>
                      </div>
                    ),
                  )
                ) : (
                  <div className="text-sm text-gray-500">
                    {t(LOCALS.daily_register_close_no_recycle_order_daily_data)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          {t(LOCALS.daily_register_close_no_recycle_order_daily_stats_today)}
        </div>
      )}
    </div>
  );
};

export default RecycleOrderDailyStatsCard;
